import { Flex, Text } from "@chakra-ui/react";
import { useState } from "react";
import AppInput from "@/features/ui/app-input";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useUpdateLineSheetSetMoq from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-moq";

interface MoqInlineEditProps {
  lineSheetSetId: number;
  value?: number | null;
  // 라인시트세트 통화. 금액 기호를 통화에 맞춰 표시한다.
  currency?: string | null;
  // 저장 성공 시 폼 값 동기화용
  onSaved: (value: number | null) => void;
  // 저장 성공 시 서버 데이터 재검증(mutate) 트리거용
  onUpdated?: () => void;
}

/**
 * MOQ 전용 인라인 편집.
 * 발행 여부/전체 편집 모드와 무관하게 에이전시가 언제든지 MOQ를 수정할 수 있다.
 */
export default function MoqInlineEdit({
  lineSheetSetId,
  value,
  currency,
  onSaved,
  onUpdated,
}: MoqInlineEditProps) {
  const identity = useIdentity();
  const { tCurrencyString } = useI18nHelper();
  const toasts = useAppToasts();
  const { fire, isLoading } = useUpdateLineSheetSetMoq();
  const [isEditing, setIsEditing] = useState(false);
  const [input, setInput] = useState("");

  const startEdit = () => {
    setInput(value != null ? String(value) : "");
    setIsEditing(true);
  };

  const save = () => {
    if (!identity?.company || identity.company.type !== "AGENCY" || isLoading) {
      return;
    }
    const moq = input !== "" ? parseInt(input, 10) : null;
    fire(
      { agencyId: identity.company.id, lineSheetSetId },
      { moq },
      { rethrow: true }
    )
      .then((response) => {
        toasts.info("MOQ가 수정되었습니다.");
        // 유로 세트는 비우거나 0 을 넣으면 서버가 기본값(5,000)으로 저장한다 — 저장된 값을 보여 준다.
        onSaved(response ? response.moq ?? null : moq);
        setIsEditing(false);
        // 서버 데이터(라인시트세트) 재검증 — 수정한 MOQ가 즉시 반영되도록
        onUpdated?.();
      })
      .catch((e) => {
        toasts.error(e);
      });
  };

  if (!isEditing) {
    return (
      <Flex alignItems={"center"} gap={"6px"}>
        <Text fontSize={"12px"}>
          {value != null
            ? tCurrencyString(value, {
                type: "SYMBOL",
                code: currency ?? undefined,
              })
            : "-"}
        </Text>
        <Text
          fontSize={"12px"}
          color={"#1272EF"}
          cursor={"pointer"}
          onClick={startEdit}
        >
          수정
        </Text>
      </Flex>
    );
  }

  return (
    <Flex alignItems={"center"} gap={"4px"}>
      <AppInput
        type={"number"}
        width={"72px"}
        value={input}
        onChange={(v) => setInput(v ?? "")}
      />
      <AppButton
        size={"small"}
        variant={"primary"}
        isLoading={isLoading}
        onClick={save}
      >
        저장
      </AppButton>
      <AppButton
        size={"small"}
        variant={"gray"}
        onClick={() => setIsEditing(false)}
      >
        취소
      </AppButton>
    </Flex>
  );
}
